import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import useRedirect from 'hooks/navigation/useRedirect';
import { changeLanguage, signOut } from 'store/entities/user/thunks';
import { languages } from 'i18n';

import { Layout, Menu, Row, Col, Affix, Space, Button, Dropdown } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { ReactComponent as NewCreditLogo } from 'assets/img/icons/newCreditLogo.svg';
import { ReactComponent as MonitoringIcon } from 'assets/img/icons/monitoring.svg';
import { ReactComponent as SpiderwebIcon } from 'assets/img/icons/spiderweb.svg';
import { ReactComponent as AdministrationIcon } from 'assets/img/icons/administration.svg';
import { ReactComponent as FeedbackBell } from 'assets/img/icons/feedback-bell.svg';
import { ReactComponent as DataSearch } from 'assets/img/icons/dataSearch.svg';
import { ReactComponent as ProfileIcon } from 'assets/img/icons/profile.svg';
import { ReactComponent as ArrowDown } from 'assets/img/icons/chevron-down.svg';
import { ReactComponent as ArrowUp } from 'assets/img/icons/chevron-up.svg';
import { ReactComponent as FileExport } from 'assets/img/icons/file-export.svg';
import CompanySearchForm from '../views/Search/CompanySearch/CompanySearchForm';
import ForeignCompanySearchForm from '../views/Search/ForeignCompanySearch/CompanySearchForm';
import IndividualSearchForm from '../views/Search/IndividualSearch/IndividualSearchForm';
import SearchForm from '../views/OtherProducts/Spiderweb/SearchBlock/SearchForm';
import { scrollToSection } from '../utils/dom';
import {
  setActivePdfModal,
  setActiveTab,
  setExpertReportModalOpen,
  setSelectedTab,
} from '../store/entities/professionalReport';
import { DownloadExcel, DownloadPdfBtn } from './reusable/report/buttons';
import { expertEvaluation, getProfessionalReportPdf } from '../store/entities/professionalReport/thunks';
import { useLocation, useParams } from 'react-router-dom';
import { setFeedbackModalActive } from '../store/entities/otherProducts';
import PermissionedTabs from './reusable/PermissionedTabs';
import { getDebtsExcel } from '../store/entities/administration/thunks';
import { setDebtUploadModalVisible } from '../store/entities/administration';

const { Header: LayoutHeader } = Layout;

const Header = () => {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const { orderId, activeTab, summary, basicData } = useSelector(state => state.professionalReport);
  const location = useLocation();
  const { companyOrPersonDebtsLoading, debtsExcelLoading } = useSelector(state => state.administration);
  const redirect = useRedirect();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { isAuthenticated, hasSession, session } = useSelector(state => state.user);
  const handleSignOut = async () => {
    await dispatch(signOut());
    redirect('/');
  };
  const handleChangeLanguage = async languageCode => {
    if (isAuthenticated) {
      const changeResult = await dispatch(changeLanguage(languageCode));

      if (changeLanguage.fulfilled.match(changeResult)) {
        i18n.changeLanguage(languageCode);
      }
    } else {
      i18n.changeLanguage(languageCode);
    }
  };

  const handleLanguageDropdown = flag => {
    setDropdownOpen(flag);
  };

  const menu = (
    <Menu>
      {languages.map(({ codeShort, codeLong }) => (
        <Menu.Item key={`lang-${codeShort}`} onClick={() => handleChangeLanguage(codeShort)}>
          {codeLong}
        </Menu.Item>
      ))}
    </Menu>
  );

  const userMenu = (
    <Menu>
      {session?.isTermsAndConditionsAccepted && (
        <Menu.Item key="setting:1" onClick={() => redirect('/profile')}>
          {t('header.settings')}
        </Menu.Item>
      )}
      <Menu.Item key="setting:2" onClick={handleSignOut}>
        {t('header.signOut')}
      </Menu.Item>
    </Menu>
  );

  const mainTabs = [
    {
      permission: true,
      key: '/search',
      icon: <DataSearch className="icon--md" />,
      link: '/search',
      label: t('header.mainNavigation.search'),
      childTabs: [
        {
          permission: true,
          key: '/search/company',
          link: '/search/company',
          label: t('companySearch.tab'),
        },
        {
          permission: 'webui_individual',
          key: '/search/individual',
          link: '/search/individual',
          label: t('individualSearch.tab'),
        },
        {
          permission: '/product/foreigncompanyreport/',
          key: '/search/foreignCompany',
          link: '/search/foreignCompany',
          label: t('foreignCompanySearch.tab'),
        },
      ],
    },
    {
      permission: '/product/monitoring/',
      key: '/monitorings',
      icon: <MonitoringIcon className="icon--md" />,
      link: '/monitorings',
      label: t('header.mainNavigation.monitorings'),
      branches: [
        {
          match: '/monitorings/credit-monitoring',
          childTabs: [
            {
              permission: '/product/companymonitoring/',
              key: '/monitorings/credit-monitoring/legal-entities',
              link: '/monitorings/credit-monitoring/legal-entities',
              label: t('otherProducts.tabs.legalEntities'),
            },
            {
              permission: '/product/personmonitoring/',
              key: '/monitorings/credit-monitoring/individual-person',
              link: '/monitorings/credit-monitoring/individual-person',
              label: t('otherProducts.tabs.individualPerson'),
            },
            {
              permission: true,
              key: '/monitorings/credit-monitoring/file-upload',
              link: '/monitorings/credit-monitoring/file-upload',
              label: t('otherProducts.tabs.fileUpload'),
            },
          ],
        },
        {
          match: '/monitorings/web-monitoring',
          childTabs: [
            {
              permission: '/product/companymonitoring/',
              key: '/monitorings/web-monitoring/legal-entities',
              link: '/monitorings/web-monitoring/legal-entities',
              label: t('otherProducts.tabs.legalEntities'),
            },
            {
              permission: '/product/personmonitoring/',
              key: '/monitorings/web-monitoring/individual-person',
              link: '/monitorings/web-monitoring/individual-person',
              label: t('otherProducts.tabs.individualPerson'),
            },
          ],
        },
      ],
    },
    {
      permission: '/product/spiderweb/',
      key: '/spiderweb',
      icon: <SpiderwebIcon className="icon--md" />,
      link: '/spiderweb',
      label: t('header.mainNavigation.spiderweb'),
    },
    {
      permission: 'webui_administration',
      key: '/administration',
      icon: <AdministrationIcon className="icon--md" />,
      link: '/administration',
      label: t('header.mainNavigation.administration'),
      branches: [
        {
          match: '/administration',
          childTabs: [
            {
              permission: true,
              key: '/administration/company',
              link: '/administration/company',
              label: t('otherProducts.tabs.legalEntities'),
            },
            {
              permission: true,
              key: '/administration/individuals',
              link: '/administration/individuals',
              label: t('otherProducts.tabs.individualPerson'),
            },
            {
              permission: true,
              key: '/administration/instructions',
              link: '/administration/instructions',
              label: t('otherProducts.tabs.instructions'),
            },
          ],
        },
      ],
    },
  ];

  const getActiveMainTabChildTabs = mainTabs => {
    const currentPath = window.location.pathname;

    const activeMainTab = mainTabs.find(tab => currentPath.startsWith(tab.link));

    if (!activeMainTab) return [];

    if (activeMainTab.branches) {
      const matchedBranch = activeMainTab.branches.find(branch => currentPath.startsWith(branch.match));
      return matchedBranch?.childTabs || [];
    }

    // Default case for flat childTabs
    return activeMainTab.childTabs || [];
  };

  const activeChildTabs = getActiveMainTabChildTabs(mainTabs);
  const showDownloadExcel = location?.pathname.includes('/administration/');
  const showDebtsFileUpload = location?.pathname.includes('/product/debtsfileupload/');

  const companyReportTabs = ['summary', 'basicData', 'companyRatings', 'negativeData', 'financialData'];

  const handleReportTabChange = tab => {
    scrollToSection(tab);
    dispatch(setSelectedTab(tab));
    dispatch(setActiveTab(tab));
    setTimeout(() => {
      dispatch(setSelectedTab(''));
    }, 100);
  };

  const getSearchForm = () => {
    const path = location.pathname;

    if (path.includes('/search/company')) return <CompanySearchForm />;
    if (path.includes('/search/individual')) return <IndividualSearchForm />;
    if (path.includes('/search/foreignCompany')) return <ForeignCompanySearchForm />;
    if (path.includes('/spiderweb')) return <SearchForm />;

    return null;
  };

  return (
    <LayoutHeader style={{ height: 'auto', lineHeight: 'normal' }}>
      <div className="nav-container">
        <Row gutter={[0, 8]}>
          <Col span={24} className="nav-section" style={{ padding: '8px 32px 0' }}>
            <Row>
              <Col span={12} style={{ height: 45 }} className="align-items-center">
                <a href="/">
                  <NewCreditLogo onClick={() => redirect('/')} />
                </a>
              </Col>
              <Col span={12}>
                <Row justify="end" style={{ alignItems: 'center' }}>
                  <Col className="space-right-md">
                    <Button
                      className="feedback-btn"
                      onClick={() => {
                        dispatch(setFeedbackModalActive(true));
                      }}
                      icon={<FeedbackBell />}
                    >
                      {t('header.feedback.btn')}
                    </Button>
                  </Col>
                  {hasSession ? (
                    <Col className="login-btn">
                      <Dropdown overlay={userMenu} trigger={['click']}>
                        <Button size="small" icon={<ProfileIcon style={{ fontSize: '16px' }} />}>
                          {session?.username?.toUpperCase()}
                        </Button>
                      </Dropdown>
                    </Col>
                  ) : (
                    <Col className="login-btn">
                      <Button
                        size="small"
                        icon={<ProfileIcon />}
                        onClick={() => redirect('/')}
                        style={{ fontWeight: 'bold', color: '#555' }}
                      >
                        {t('login.login')}
                      </Button>
                    </Col>
                  )}
                  <Col span={1}>
                    <div style={{ width: '2px', height: '16px', background: 'red', margin: 'auto' }}></div>
                  </Col>
                  <Col>
                    <Dropdown overlay={menu} trigger={['click']} onVisibleChange={handleLanguageDropdown}>
                      <Space style={{ cursor: 'pointer', color: '#555', fontWeight: 'bold' }}>
                        <GlobalOutlined style={{ fontSize: '16px' }} />
                        {languages.find(lang => lang.codeShort === i18n.language)?.codeLong || 'ENG'}
                        {dropdownOpen ? <ArrowUp /> : <ArrowDown />}
                      </Space>
                    </Dropdown>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
          <Col span={24} className="nav-section">
            <Row className="navigation-buttons level1" justify="end" gutter={[16, 16]}>
              <PermissionedTabs tabs={mainTabs} />
            </Row>
          </Col>

          {(activeChildTabs.length > 0 || showDownloadExcel) && (
            <Col span={24} className="nav-section">
              <Row className="navigation-buttons level2" justify="end" gutter={[16, 16]}>
                {activeChildTabs.length > 0 && <PermissionedTabs tabs={activeChildTabs} />}
                {!showDebtsFileUpload && location.pathname.includes('/administration') && (
                  <Col>
                    <Button
                      icon={<FileExport />}
                      onClick={() => dispatch(setDebtUploadModalVisible(true))}
                      size="small"
                    >
                      {t('header.administration.uploadDebts.web')}
                    </Button>
                  </Col>
                )}
                {showDownloadExcel && (
                  <Col>
                    <DownloadExcel
                      disabled={
                        companyOrPersonDebtsLoading ||
                        debtsExcelLoading ||
                        location?.pathname === '/administration/instructions'
                      }
                      fileName={
                        location?.pathname === '/administration/individuals'
                          ? 'header.breadcrumb.administration.person.xlsx'
                          : 'header.breadcrumb.administration.company.xlsx'
                      }
                      className="claims-excel-download"
                      fetcher={() =>
                        dispatch(
                          getDebtsExcel({
                            type: location?.pathname === '/administration/individuals' ? 'person' : 'company',
                          })
                        )
                      }
                    />
                  </Col>
                )}
              </Row>
            </Col>
          )}
          {getSearchForm() && (
            <Affix offsetTop={0} className="affix-style" style={{ width: '100%' }}>
              <Row gutter={[0, 8]}>
                <Col span={24} className="nav-section">
                  {getSearchForm()}
                </Col>
                {window.location.pathname.includes('/search/company') &&
                window.location.pathname.includes('/professional-report') ? (
                  <Col span={24} className="nav-section">
                    <Row
                      justify="space-between"
                      style={{ background: '#f4f4f4', margin: '0 -32px', padding: '0 32px' }}
                    >
                      <Col style={{ margin: 'auto 0' }}>
                        <Row gutter={[24]} className="report-sections-nav">
                          {companyReportTabs.map(tab => (
                            <Col
                              className={activeTab === tab ? 'active-tab' : 'inactive-tab'}
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleReportTabChange(tab)}
                              key={tab}
                            >
                              <div className="report-section">{t(`professionalReport.tab.${tab}`)}</div>
                            </Col>
                          ))}
                        </Row>
                      </Col>
                      {hasSession && (
                        <Col className="report-pdf-buttons">
                          <Row gutter={[8]}>
                            {summary?.expertEvaluation?.isAllowed && (
                              <Col>
                                <Button
                                  onClick={() => {
                                    dispatch(setExpertReportModalOpen(true));
                                    dispatch(expertEvaluation({ orderId }));
                                  }}
                                >
                                  {t('professionalReport.companyRatings.orderExpertReport.title')}
                                </Button>
                              </Col>
                            )}
                            <Col>
                              <DownloadPdfBtn
                                fetcher={() =>
                                  dispatch(
                                    getProfessionalReportPdf({
                                      companyCode: basicData?.basicInformation?.companyCode,
                                      orderId,
                                    })
                                  )
                                }
                                reportType="professional"
                              />
                            </Col>
                            <Col>
                              <DownloadPdfBtn
                                fetcher={() => dispatch(setActivePdfModal(true))}
                                text="global.customPdf"
                                reportType="professional"
                              />
                            </Col>
                          </Row>
                        </Col>
                      )}
                    </Row>
                  </Col>
                ) : null}
              </Row>
            </Affix>
          )}
        </Row>
      </div>
    </LayoutHeader>
  );
};

export default Header;
